import { BehaviorSubject, Observable, ReplaySubject, Subject, Subscription } from 'rxjs';
import * as Sentry from '@sentry/react';
import {
  IGenericSocketUpdate,
  ISocketResponseBaseDataModel,
  SocketStatus,
} from '../data-models/socket-base.data-model';
import { getWssUrl } from '../util/environment';

export interface IWebSocket {
  get socketStatus(): Observable<SocketStatus>;
  get socketStatusSync(): SocketStatus;
  disconnect: () => void;
  listen<Response extends IGenericSocketUpdate<unknown>>(
    eventName: string,
    Listener: (data: Response) => void,
    getInitialValue?: boolean
  ): Subscription;
}

export class WebSocketService implements IWebSocket {
  static readonly #MAX_RETRY_COUNT = 5;
  static #instance: IWebSocket;

  readonly #getToken;
  readonly #url;

  #retryCount = 0;
  #socket: WebSocket | undefined;
  #socketStatus: BehaviorSubject<SocketStatus>;
  #responseChannels: Map<string, ReplaySubject<unknown>>;

  private constructor(getToken: () => Promise<string>, url: string | undefined) {
    this.#getToken = getToken;
    this.#url = url;
    this.#responseChannels = new Map();
    this.#connect();
    this.#socketStatus = new BehaviorSubject<SocketStatus>(SocketStatus.connecting);
  }

  disconnect() {
    this.#responseChannels.forEach((value) => {
      value.complete();
      value.unsubscribe();
    });
    this.#responseChannels = new Map<string, ReplaySubject<unknown>>();
    this.#clearListeners();
    this.#socket?.close();
    this.#socketStatus.next(SocketStatus.closed);
  }

  listen<Response extends IGenericSocketUpdate<unknown>>(
    eventName: string,
    listener: (data: Response) => void,
    getInitialValue = true
  ) {
    let channel = this.#responseChannels.get(eventName) as Subject<Response>;
    if (!channel) {
      channel = getInitialValue ? new ReplaySubject<Response>(1) : new Subject();
      this.#responseChannels.set(eventName, channel as ReplaySubject<unknown>);
    }

    return channel.asObservable().subscribe(listener);
  }

  get socketStatus() {
    return this.#socketStatus.asObservable();
  }

  get socketStatusSync() {
    return this.#socketStatus.getValue();
  }

  async #connect() {
    const token = await this.#getToken();
    this.#socket = new WebSocket(this.#url ?? this.#getConnectionUrl(token));
    this.#socket.addEventListener('open', this.#onSocketOpen);
    this.#socket.addEventListener('close', this.#onSocketClose);
    this.#socket.addEventListener('error', this.#onSocketError);
    this.#socket.addEventListener('message', this.#onSocketMessage);
  }

  #retryConnect() {
    if (this.#retryCount < WebSocketService.#MAX_RETRY_COUNT) {
      this.#socketStatus.next(SocketStatus.connecting);
      this.#retryCount++;
      Sentry.logger.info(`[WebSocketService] retrying connection to websocket, count ${this.#retryCount}.`);

      setTimeout(() => {
        this.#connect();
      }, this.#retryCount * 1000);
    } else {
      console.error(
        `[WebSocketService] Failed to establish connection after ${
          WebSocketService.#MAX_RETRY_COUNT
        } attempts.`
      );
      this.#socketStatus.next(SocketStatus.error);
    }
  }

  #clearListeners() {
    if (this.#socket) {
      this.#socket.removeEventListener('open', this.#onSocketOpen);
      this.#socket.removeEventListener('close', this.#onSocketClose);
      this.#socket.removeEventListener('error', this.#onSocketError);
      this.#socket.removeEventListener('message', this.#onSocketMessage);
    }
  }

  #getConnectionUrl(authToken: string): string {
    return `${getWssUrl()}?token=${authToken}`;
  }

  #onSocketClose = (closeEvent: CloseEvent) => {
    Sentry.logger.info('[WebSocketService]  Socket closed - disconnected', { closeEvent });
    this.#clearListeners();

    // See iana.org/assignments/websocket/websocket.xhtml for details.
    if (closeEvent.code > 1000 || !closeEvent.wasClean) {
      this.#retryConnect();
    } else {
      this.#socketStatus.next(SocketStatus.closed);
    }
  };

  #onSocketError = (errorEvent: Event) => {
    console.debug('[WebSocketService] Socket error', JSON.stringify(errorEvent));
    this.#clearListeners();
    this.#socketStatus.next(SocketStatus.error);
    this.#retryConnect();
  };

  #onSocketMessage = (event: MessageEvent) => {
    const data: unknown = this.#readMessage(event.data);

    if (!(data as ISocketResponseBaseDataModel).type) {
      console.error('[WebSocketService] unexpected message', data);
      return;
    }

    const response = data as ISocketResponseBaseDataModel;
    const channel = this.#responseChannels.get(response.type);
    channel?.next(response);
  };

  #onSocketOpen = () => {
    Sentry.logger.info('[WebSocketService] Socket opened - connected');
    this.#retryCount = 0;
    this.#socketStatus.next(SocketStatus.ready);
  };

  #readMessage<T>(msg: string) {
    return JSON.parse(msg) as T;
  }

  static async initService(
    getToken: () => Promise<string>,
    url: string | undefined = undefined
  ): Promise<IWebSocket> {
    if (WebSocketService.#instance && WebSocketService.#instance.socketStatusSync !== SocketStatus.closed) {
      Sentry.logger.info('[WebSocketService] Socket has already been initialized, skipping');
    } else {
      WebSocketService.#instance = new WebSocketService(getToken, url);
    }

    return WebSocketService.#instance;
  }

  static destroyService() {
    if (WebSocketService.#instance) {
      WebSocketService.#instance.disconnect();
    }
  }

  static get() {
    return WebSocketService.#instance;
  }
}
